import { ButtonGradientPrimary, Colors, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { ObjectiveOrDraft } from '@/types/objectives';

import { EmployeeObjectivesList } from '@/components/objectives/EmployeeObjectivesList';
import { useAmelioTranslations } from '@/hooks';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectiveOwners } from '@/hooks/objectives/useObjectiveOwners';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { newObjectivePath, stepBackState } from '@/navigation/objectivesRoutes';

interface YourObjectivesProps {
    objectives: ObjectiveOrDraft[];
    isLoading: boolean;
    isError: boolean;
    /** The selected cycle, only to send the reader back to page one when they change it. */
    periodId: string;
}

/**
 * "Your objectives": the reader's own objectives in the selected period, each card a link to the
 * objective, and -- for whoever may write one -- the way to create one without leaving the portal.
 */
export const YourObjectives = ({
    objectives,
    isLoading,
    isError,
    periodId,
}: YourObjectivesProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { canCreateObjectives } = useObjectiveAbilities();
    // A new period starts back on page one: page four of the previous period may not exist.
    const [pagination, setPagination] = useState({ periodId, page: 1 });
    const page = pagination.periodId === periodId ? pagination.page : 1;
    const { employeesById, isLoading: areEmployeesLoading } = useObjectiveOwners(objectives, '');

    const heading = (
        <div className={'flex flex-wrap items-center justify-between gap-4'}>
            <div className={'flex items-center gap-3'}>
                <span
                    aria-hidden
                    className={
                        'bg-obj-vision-tint flex size-[44px] shrink-0 items-center justify-center rounded-xl'
                    }
                >
                    <Icon
                        name={'cryptocompany-target'}
                        size={24}
                        colorClassName={'text-violet'}
                        strokeWidth={1.8}
                        ariaHidden
                    />
                </span>

                <div>
                    <h2 className={'text-navy text-[20px] font-medium'}>
                        {t('employeePortal.objectives.list.title')}
                    </h2>

                    <Text color={Colors.BASE_400}>
                        {t('employeePortal.objectives.list.subtitle')}
                    </Text>
                </div>
            </div>

            {canCreateObjectives && (
                <ButtonGradientPrimary
                    onClick={() =>
                        navigate({
                            path: newObjectivePath({ period: periodId }),
                            state: stepBackState({ forReader: true }),
                        })
                    }
                    size={'md'}
                    fitContent
                    leftIcon={
                        <Icon
                            name={'essential-add'}
                            size={'md'}
                            color={Colors.WHITE}
                        />
                    }
                    dataTestId={'employee-portal-create-objective'}
                >
                    {t('objectives.createObjective')}
                </ButtonGradientPrimary>
            )}
        </div>
    );

    return (
        <EmployeeObjectivesList
            objectives={objectives}
            employeesById={employeesById}
            isLoading={isLoading || areEmployeesLoading}
            page={page}
            onPageChange={(nextPage) => setPagination({ periodId, page: nextPage })}
            heading={heading}
            emptyMessage={t('employeePortal.objectives.list.empty')}
            errorMessage={isError ? t('employeePortal.objectives.list.loadError') : undefined}
            dataTestId={'employee-portal-objectives-list'}
        />
    );
};
